import type { TFunction } from 'i18next';

interface HasConfig {
  kind: string;
  config: Record<string, unknown>;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** Host and path with the secret-looking parts cut: `hooks.slack.com/services/T04…/B07…/••••`. */
export function maskUrl(raw: string): string {
  try {
    const url = new URL(raw);
    const parts = url.pathname.split('/').filter(Boolean);
    const masked = parts.map((p, i) => {
      if (i === parts.length - 1 && (parts.length > 1 || p.length > 6)) return '••••';
      if (i === 0 || p.length <= 6) return p;
      return `${p.slice(0, 3)}…`;
    });
    return [url.host, ...masked].join('/');
  } catch {
    return '';
  }
}

/** Where deliveries go, safe to show: a masked URL, the Send to addresses, or the vendor account. */
export function integrationTarget(row: HasConfig, t: TFunction): string {
  const c = row.config ?? {};
  switch (row.kind) {
    case 'email': {
      const to = Array.isArray(c.to) ? (c.to as unknown[]).map(str).filter(Boolean) : [];
      return to.length ? t('integrations.target.sendsTo', { to: to.join(', ') }) : '';
    }
    case 'api_pipedrive':
      return str(c.company_domain);
    case 'api_mailchimp':
      return c.list_id ? t('integrations.target.audience', { id: str(c.list_id) }) : '';
    case 'api_kit':
    case 'formshive_endpoint':
      return c.form_id ? t('integrations.target.form', { id: str(c.form_id) }) : '';
    default: {
      const url = str(c.url) || str(c.webhook_url);
      return url ? maskUrl(url) : '';
    }
  }
}

/** `retry` and `dlq` are failed attempts; `success` and `replayed` are not. */
export function isFailedOutcome(outcome: string | undefined | null): boolean {
  return outcome === 'retry' || outcome === 'dlq' || outcome === 'failed';
}

interface Attempt {
  outcome: string;
  http_status?: number | null;
}

/** The status to show beside a failure: the failing attempt's, never that of a later success. */
export function failureStatus(row: {
  last_delivery?: Attempt | null;
  last_failure?: Attempt | null;
}): number | undefined {
  const attempt =
    row.last_failure ??
    (row.last_delivery && isFailedOutcome(row.last_delivery.outcome) ? row.last_delivery : null);
  return attempt?.http_status || undefined;
}

/** "A, B, and 4 more" from the first three titles and the full count. */
export function usedByTitles(titles: string[] | undefined, count: number, t: TFunction): string {
  const shown = (titles ?? []).slice(0, 3);
  const rest = count - shown.length;
  if (shown.length === 0) return '';
  if (rest > 0)
    return t('integrations.index.titlesAndMore', { titles: shown.join(', '), count: rest });
  return shown.join(', ');
}

export interface PayloadExcerpt {
  /** Pretty-printed JSON when it parses, else the text as stored. */
  text: string;
  /** The stored excerpt was cut; `size` is the full body in bytes when known. */
  truncated: boolean;
  size?: number;
  /** The parsed body, when it was JSON. */
  json?: unknown;
}

const pretty = (raw: string): { text: string; json?: unknown } => {
  try {
    const json: unknown = JSON.parse(raw);
    return { text: JSON.stringify(json, null, 2), json };
  } catch {
    return { text: raw };
  }
};

/**
 * A delivery's `payload_excerpt`: the body itself, or `{_preview, _size_bytes, _truncated}`
 * where `_preview` is the start of the body as a string.
 */
export function payloadExcerpt(excerpt: unknown): PayloadExcerpt | null {
  if (excerpt === undefined || excerpt === null) return null;
  if (typeof excerpt === 'string') return { ...pretty(excerpt), truncated: false };
  if (typeof excerpt === 'object' && !Array.isArray(excerpt) && '_preview' in excerpt) {
    const w = excerpt as { _preview?: unknown; _size_bytes?: unknown; _truncated?: unknown };
    const preview = typeof w._preview === 'string' ? w._preview : JSON.stringify(w._preview);
    return {
      ...pretty(preview),
      truncated: w._truncated === true,
      size: typeof w._size_bytes === 'number' ? w._size_bytes : undefined,
    };
  }
  return { text: JSON.stringify(excerpt, null, 2), json: excerpt, truncated: false };
}

type DeliveryLike = {
  outcome: string;
  next_attempt_at?: string | null;
  form_id?: string | null;
  message_id?: string | null;
  payload_excerpt?: unknown;
  is_test?: boolean;
  payload_available?: boolean;
};

/** Sent with "Send a test". Without the server's `is_test`: no form or message, or a body marked `_test`. */
export function isTestDelivery(r: DeliveryLike): boolean {
  if (r.is_test !== undefined) return r.is_test;
  const json = payloadExcerpt(r.payload_excerpt)?.json;
  if (json && typeof json === 'object' && (json as { _test?: unknown })._test === true) return true;
  return !r.form_id && !r.message_id;
}

export type AttemptResult = 'delivered' | 'replayed' | 'retrying' | 'retried' | 'failed';

/** A `retry` attempt is still retrying while another attempt is scheduled, else it was retried. */
export function attemptResult(r: DeliveryLike): AttemptResult {
  if (r.outcome === 'success') return 'delivered';
  // A test is recorded as `replayed` (it is sent manually); it was not sent again.
  if (r.outcome === 'replayed') return isTestDelivery(r) ? 'delivered' : 'replayed';
  if (r.outcome === 'retry') return r.next_attempt_at ? 'retrying' : 'retried';
  return 'failed';
}

/** The status and the error text, without repeating a status the text already names. */
export function responseLine(r: { http_status?: number | null; error_detail?: string | null }) {
  const status = r.http_status ? String(r.http_status) : '';
  const detail = r.error_detail ?? '';
  if (status && detail.includes(status)) return detail;
  return [status, detail].filter(Boolean).join(' ');
}

/** Why a manual send failed, for its toast: the error text, else the HTTP status or class. */
export function dispatchDetail(r: {
  error?: string | null;
  http_status?: number | null;
  error_class?: string | null;
  outcome: string;
}): string {
  if (r.error) return r.error;
  if (r.http_status) return `HTTP ${r.http_status}`;
  return r.error_class ?? r.outcome;
}

/** A message delivery whose event has been swept, so it cannot be resent. */
export function isPayloadGone(r: DeliveryLike): boolean {
  return !isTestDelivery(r) && r.payload_available === false;
}
